import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

import type { Skeleton } from '../../../../lib/skeleton/core';
import { AXIS_COLOR_X, AXIS_COLOR_Y, AXIS_COLOR_Z } from '../../constants';
import {
  clampManipulatorScale,
  computeTransformControlsScale,
} from '../../shared';

interface SelectionOrientationManipulatorProps {
  skeleton: Skeleton;
  selectedBoneIndex: number | null;
  visible: boolean;
  manipulatorScale: number;
}

const AXIS_GIZMO_BASE_SIZE = 0.65;
const AXIS_CONE_HEIGHT = 0.6;
const AXIS_CONE_RADIUS = 0.05;
const AXIS_CONE_RADIAL_SEGMENTS = 18;
const AXIS_CONE_BASE_OFFSET = 0.18;
const AXIS_GIZMO_OPACITY = 0.82;
const AXIS_GIZMO_RENDER_ORDER = 998;
const AXIS_GIZMO_SCALE_MULTIPLIER = 0.72;

function buildAxisConeGeometry() {
  const geometry = new THREE.ConeGeometry(
    AXIS_CONE_RADIUS,
    AXIS_CONE_HEIGHT,
    AXIS_CONE_RADIAL_SEGMENTS,
  );
  geometry.translate(0, AXIS_CONE_BASE_OFFSET + AXIS_CONE_HEIGHT * 0.5, 0);
  return geometry;
}

function AxisCone({
  geometry,
  color,
  rotation,
}: {
  geometry: THREE.BufferGeometry;
  color: number;
  rotation: [number, number, number];
}) {
  return (
    <mesh
      geometry={geometry}
      rotation={rotation}
      renderOrder={AXIS_GIZMO_RENDER_ORDER}
      frustumCulled={false}
    >
      <meshBasicMaterial
        color={color}
        side={THREE.DoubleSide}
        transparent
        opacity={AXIS_GIZMO_OPACITY}
        depthTest={false}
        depthWrite={false}
        toneMapped={false}
      />
    </mesh>
  );
}

export default function SelectionOrientationManipulator({
  skeleton,
  selectedBoneIndex,
  visible,
  manipulatorScale,
}: SelectionOrientationManipulatorProps) {
  const anchorRef = useRef<THREE.Group>(null);
  const camera = useThree((state) => state.camera);
  const neutralScale = useMemo(() => new THREE.Vector3(1, 1, 1), []);
  const geometry = useMemo(() => buildAxisConeGeometry(), []);
  const safeManipulatorScale = clampManipulatorScale(manipulatorScale);

  const hasSelection =
    selectedBoneIndex !== null &&
    selectedBoneIndex >= 0 &&
    selectedBoneIndex < skeleton.bones.length;

  useEffect(
    () => () => {
      geometry.dispose();
    },
    [geometry],
  );

  useFrame(() => {
    if (!visible || !hasSelection) return;
    const anchor = anchorRef.current;
    if (!anchor || selectedBoneIndex === null) return;

    const selectedBone = skeleton.bone(selectedBoneIndex);
    selectedBone.posedWorld.decompose(
      anchor.position,
      anchor.quaternion,
      neutralScale,
    );
    const scale =
      computeTransformControlsScale(camera, anchor.position) *
      safeManipulatorScale *
      AXIS_GIZMO_SCALE_MULTIPLIER;
    anchor.scale.setScalar(AXIS_GIZMO_BASE_SIZE * scale);
    anchor.updateMatrixWorld(true);
  });

  if (!visible || !hasSelection) return null;

  return (
    <group ref={anchorRef} frustumCulled={false}>
      <AxisCone geometry={geometry} color={AXIS_COLOR_X} rotation={[0, 0, -Math.PI / 2]} />
      <AxisCone geometry={geometry} color={AXIS_COLOR_Y} rotation={[0, 0, 0]} />
      <AxisCone geometry={geometry} color={AXIS_COLOR_Z} rotation={[Math.PI / 2, 0, 0]} />
    </group>
  );
}
